import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Bookmark, Clock, Globe, MapPin, PencilLine, Phone, Trash2 } from "lucide-react";
import { useSearch } from "../contexts/SearchContext";
import { useCart } from "../contexts/CartContext";
import { useOrg } from "../contexts/OrganizationContext";
import { useBodyClass } from "../hooks/useBodyClass";
import { useContentTop } from "../hooks/useContentTop";
import { searchService } from "../services/searchService";
import { parseLatLng, normalizeWebsite, haversine } from "../utils/geo";
import { sanitizedHtml } from "../utils/sanitize";
import AgencyInfo from "../components/Search/Detail/AgencyInfo";
import Articles from "../components/Search/Detail/Articles";
import ServiceAreas from "../components/Search/Detail/ServiceAreas";
import ShareResult from "../components/Search/Detail/ShareResult";
import ClaimAgencyModal from "../components/Search/Detail/ClaimAgencyModal";
import SendReferral from "../components/Search/Detail/SendReferral";
import ListingUpdateTypeSelection from "../components/Search/Detail/ListingUpdateTypeSelection";
import ProgramSiteUserNoteForm from "../components/Search/Detail/ProgramSiteUserNoteForm";
import UserNotes from "../components/Search/Detail/UserNotes";
import AddToList from "../components/Search/AddToList";
import CreateAccountCta from "../components/Search/CreateAccountCta";
import HeaderCartButton from "../components/Search/HeaderCartButton";

function DetailInfoBlock({ label, children, className = "" }) {
  if (!children) return null;

  return (
    <div className={`search-detail-info-block${className ? ` ${className}` : ""}`}>
      <h3 className="search-detail-section-title">{label}</h3>
      <div className="search-detail-section-body">{children}</div>
    </div>
  );
}

function DetailDataItem({ label, children, className = "" }) {
  if (!children) return null;

  return (
    <div className={`search-detail-data-item${className ? ` ${className}` : ""}`}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function DetailActionLink({ href, onClick, icon: Icon, label }) {
  return (
    <a
      className="search-detail-action-link"
      href={href}
      onClick={onClick}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="search-detail-action-icon"><Icon aria-hidden="true" size={18} strokeWidth={1.9} /></span>
      <span className="search-detail-action-label">{label}</span>
    </a>
  );
}

function DetailActionButton({ onClick, icon: Icon, label }) {
  return (
    <button className="search-detail-action-link" type="button" onClick={onClick}>
      <span className="search-detail-action-icon"><Icon aria-hidden="true" size={18} strokeWidth={1.9} /></span>
      <span className="search-detail-action-label">{label}</span>
    </button>
  );
}

function isSearchResultsUrl(url) {
  return typeof url === "string" && url.startsWith("/search?");
}

function getBackAction({ backUrl, backLabel, fromCart, lastSearchUrl }) {
  const defaultLabel = fromCart ? "Back to List" : "Back to Results";

  if (backUrl) return { url: backUrl, label: backLabel || defaultLabel };
  if (fromCart) return { url: "/cart", label: "Back to List" };
  if (isSearchResultsUrl(lastSearchUrl)) return { url: lastSearchUrl, label: "Back to Results" };

  return { url: "/", label: "Start a New Search" };
}

function SearchResultDetailPage({ fromCart = false, autoPrint = false, overrideId, backUrl, backLabel }) {
  const params = useParams();
  const id = overrideId || params.id;
  const navigate = useNavigate();
  const { resultForId, params: searchParams } = useSearch();
  const { contains, addItem, removeItem, itemCount } = useCart();
  const { organization } = useOrg();

  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [claimModalOpen, setClaimModalOpen] = React.useState(false);
  const [showUpdateTypeModal, setShowUpdateTypeModal] = React.useState(false);
  const [showNoteFormModal, setShowNoteFormModal] = React.useState(false);
  const [userNotesKey, setUserNotesKey] = React.useState(0);
  const [showAccountCta, setShowAccountCta] = React.useState(false);
  const isSignedIn = !!window.UW211?.CURRENT_USER;

  useBodyClass("page-search");
  useContentTop();

  // Auto-open claim modal if user signed in after clicking "Claim Agency"
  React.useEffect(() => {
    const savedResourceNumber = sessionStorage.getItem("resource_number");
    if (isSignedIn && result && String(result.program_site_id) === savedResourceNumber) {
      setClaimModalOpen(true);
      sessionStorage.removeItem("resource_number");
    }
  }, [isSignedIn, result]);

  React.useEffect(() => {
    setLoading(true);
    searchService.getResult(id).then((data) => {
      setResult(data);
      setLoading(false);
    }).catch(() => {
      // Fall back to cached result if API fails
      const cached = resultForId(id);
      if (cached) {
        setResult(cached);
      }
      setLoading(false);
    });
  }, [id]);

  React.useEffect(() => {
    if (autoPrint && result && !loading) {
      const timer = setTimeout(() => window.print(), 1000);
      return () => clearTimeout(timer);
    }
  }, [autoPrint, result, loading]);

  if (loading) {
    return (
      <div className="search-layout">
        <div className="sidebar">
          <div className="loading-overlay" style={{ display: "block" }}>
            <div className="loading-container">
              <div className="loading-content">
                <div className="preloader"><div className="dot1" /><div className="dot2" /></div>
                <p className="direct">Loading</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="search-layout">
        <div className="sidebar">
          <div className="no-results">
            <p>Resource not found.</p>
            <a href="/">Start a new search</a>
          </div>
        </div>
      </div>
    );
  }

  const latLng = result.latLng || parseLatLng(result.location || result.site_location);
  const addressComponents = [
    result.address_1,
    result.address_2,
    result.city,
    result.state,
  ].filter(Boolean);
  const addressLine = addressComponents.join(", ");
  // Phone priority: program_site > program > site > agency (matching Marionette)
  const phone = result.program_site_phones?.phone_1
    || result.program_site_phones?.phone_2
    || result.program_phones?.phone_1
    || result.site_phones?.phone_1
    || result.agency_phones?.phone_1
    || null;
  const tollFreePhone = result.program_site_phones?.phone_toll_free
    || result.program_phones?.phone_toll_free
    || result.site_phones?.phone_toll_free
    || result.agency_phones?.phone_toll_free
    || null;
  const website = normalizeWebsite(result.program_site_website || result.program_website || result.site_website || result.agency_website || result.website || "");
  const inCart = contains(result);
  const notesEnabled = organization.userFeatures?.notes || window.UW211?.ORGANIZATION?.userFeatures?.notes;
  const serviceAreasEnabled = organization.flags?.enableServiceAreas || window.UW211?.ORGANIZATION?.flags?.enableServiceAreas;
  const showSuggestionLinks = organization.flags?.showUserSuggestionLinks || window.UW211?.ORGANIZATION?.flags?.showUserSuggestionLinks;
  const showClaimAgency = organization.flags?.showClaimAgency || window.UW211?.ORGANIZATION?.flags?.showClaimAgency;
  const publicAddress = !result.private_physical_address && addressComponents.length > 0;
  const directionsUrl = latLng
    ? `https://www.google.com/maps/dir/?api=1&destination=${latLng.lat},${latLng.lng}`
    : null;
  const referralEmails = typeof result.referral_emails === "string"
    ? (() => {
      try {
        return JSON.parse(result.referral_emails);
      } catch {
        return result.referral_emails;
      }
    })()
    : result.referral_emails;
  const hasReferralEmails = Array.isArray(referralEmails) ? referralEmails.length > 0 : !!referralEmails;
  const showReferral = hasReferralEmails && (organization.userFeatures?.referrals || window.UW211?.ORGANIZATION?.userFeatures?.referrals);
  const backAction = getBackAction({
    backUrl,
    backLabel,
    fromCart,
    lastSearchUrl: sessionStorage.getItem("lastSearchUrl"),
  });

  const distance = searchParams.coords && latLng
    ? haversine(searchParams.coords, latLng, { unit: "mile" })
    : null;

  const displayTitle = result.name || result.agency_name;
  const displayAgency = result.agency_name && result.agency_name !== displayTitle ? result.agency_name : null;
  const displaySite = result.site_name && result.site_name !== result.agency_name ? result.site_name : null;
  const summaryFacts = [
    publicAddress ? {
      label: "Address",
      icon: MapPin,
      value: <span dangerouslySetInnerHTML={sanitizedHtml(addressLine)} />,
    } : null,
    phone ? {
      label: "Phone",
      icon: Phone,
      value: <a href={`tel:${phone.replace(/\D/g, "")}`} onClick={() => searchService.trackActivity({ action: "phone", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })}>{phone}</a>,
    } : null,
    result.hours_of_operation ? {
      label: "Hours",
      icon: Clock,
      value: result.hours_of_operation,
    } : null,
  ].filter(Boolean);
  const distanceLabel = distance != null ? `${distance.toFixed(1)} miles away` : null;
  const showFeedbackBlock = (showSuggestionLinks && ((isSignedIn && notesEnabled) || organization.links?.claimResource)) || showReferral;
  const detailItems = [
    phone ? {
      label: "Phone",
      content: <a href={`tel:${phone.replace(/\D/g, "")}`} onClick={() => searchService.trackActivity({ action: "phone", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })}>{phone}</a>,
    } : null,
    website ? {
      label: "Website",
      content: <a href={website} target="_blank" rel="noopener noreferrer" onClick={() => searchService.trackActivity({ action: "website", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })}>{website}</a>,
    } : null,
    result.fee_structure_source ? {
      label: "Fees",
      content: <span dangerouslySetInnerHTML={sanitizedHtml(result.fee_structure_source)} />,
    } : null,
    serviceAreasEnabled ? {
      label: "Service Areas",
      content: <ServiceAreas programSiteId={result.program_site_id} />,
      className: "search-detail-data-item--service-areas",
    } : null,
    result.site_disabilities_access || result.disabilities_access ? {
      label: "Disability Access",
      content: <span dangerouslySetInnerHTML={sanitizedHtml(result.site_disabilities_access || result.disabilities_access)} />,
    } : null,
    result.application_process ? {
      label: "Application Process",
      content: <span dangerouslySetInnerHTML={sanitizedHtml(result.application_process)} />,
    } : null,
    result.hours_of_operation ? {
      label: "Hours",
      content: result.hours_of_operation,
    } : null,
    tollFreePhone && tollFreePhone !== phone ? {
      label: "Toll-Free",
      content: <a href={`tel:${tollFreePhone.replace(/\D/g, "")}`}>{tollFreePhone}</a>,
    } : null,
    result.email ? {
      label: "General Email",
      content: <a href={`mailto:${result.email}`} onClick={() => searchService.trackActivity({ action: "email", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })}>{result.email}</a>,
    } : null,
    result.documents_required ? {
      label: "Documents Required",
      content: <span dangerouslySetInnerHTML={sanitizedHtml(result.documents_required)} />,
    } : null,
    result.languages ? {
      label: "Languages Offered",
      content: <span dangerouslySetInnerHTML={sanitizedHtml(result.languages)} />,
    } : null,
    result.contact_name || result.contact_title || result.contact_email ? {
      label: "Service Contact",
      content: (
        <>
          {result.contact_name && <p>{result.contact_name}</p>}
          {result.contact_title && <p>{result.contact_title}</p>}
          {result.contact_email && <p><a href={`mailto:${result.contact_email}`}>{result.contact_email}</a></p>}
        </>
      ),
    } : null,
    result.wheelchair_accessibility ? {
      label: "Wheelchair Accessibility",
      content: <span dangerouslySetInnerHTML={sanitizedHtml(result.wheelchair_accessibility)} />,
    } : null,
    result.tdd_phone ? {
      label: "TDD",
      content: result.tdd_phone,
    } : null,
    result.tips ? {
      label: "Helpful Tips",
      content: <span dangerouslySetInnerHTML={sanitizedHtml(result.tips)} />,
    } : null,
    result.immigrants_without_documentation != null ? {
      label: "Available to persons w/o documentation",
      content: result.immigrants_without_documentation === true || result.immigrants_without_documentation === "true" ? "Yes" : "No",
      className: "immigrants_without_documentation",
    } : null,
    result.wait_list ? {
      label: "Wait List",
      content: <span dangerouslySetInnerHTML={sanitizedHtml(result.wait_list)} />,
    } : null,
    result.insurance_accepted ? {
      label: "Insurance Accepted",
      content: <span dangerouslySetInnerHTML={sanitizedHtml(result.insurance_accepted)} />,
    } : null,
  ].filter(Boolean);

  function handleCartToggle() {
    if (inCart) {
      removeItem(result);
    } else {
      if (itemCount === 0) setShowAccountCta(true);
      addItem(result);
    }
  }

  function handleBack() {
    navigate(backAction.url);
  }

  function getReportIssueLink() {
    const org = organization || window.UW211?.ORGANIZATION;
    const claimResourceUrl = org?.links?.claimResource;
    if (!claimResourceUrl) return null;

    if (org.serviceLocale?.abbr === "CT") {
      const params = new URLSearchParams({
        agency_Name: result.agency_name || '',
        program_Name: result.name || '',
        resource_ID: result.program_site_id || '',
        location_Name: result.site_name || ''
      });
      return `${claimResourceUrl}?${params.toString()}`;
    }
    return claimResourceUrl;
  }

  function handleUpdateListing(e) {
    e.preventDefault();

    if (isSignedIn && notesEnabled) {
      // Show modal with choice between note and external form
      setShowUpdateTypeModal(true);
    } else {
      // Not logged in or notes disabled - go straight to external form
      const reportIssueLink = getReportIssueLink();
      if (reportIssueLink) {
        window.open(reportIssueLink, '_blank', 'noopener,noreferrer');
      }
    }
  }

  function handleShowNoteForm() {
    setShowUpdateTypeModal(false);
    setShowNoteFormModal(true);
  }

  function handleNoteCreated() {
    setUserNotesKey(prev => prev + 1);
  }

  function handleClaimAgency(e) {
    e.preventDefault();
    const uw211 = window.UW211 || {};
    const user = uw211.CURRENT_USER;
    if (!user) {
      // Not logged in: save context and redirect to sign in
      sessionStorage.setItem("lastSearchUrl", `/search/${result.program_site_id || id}`);
      sessionStorage.setItem("resource_number", result.program_site_id || id);
      window.location = "/users/sign_in?from_claim_agency_modal=1";
      return;
    }
    const isNonDeniedAgencyUser = uw211.IS_NON_DENIED_AGENCY_USER || false;
    const userAgency = user.get ? user.get("agency") : user.agency;
    // If user already owns THIS agency, go to agency data page
    if (isNonDeniedAgencyUser && userAgency && String(userAgency.number) === String(result.agency_number)) {
      window.location = "/my211/agency_data";
      return;
    }
    // Otherwise open the modal (handles both "owns different agency" and "no agency" cases)
    setClaimModalOpen(true);
  }

  return (
    <div className="search-layout search-layout--detail-page">
      <div className="sidebar search-detail-sidebar">
        <div className="results-content search-detail-results-content">
          <div className="search-detail-top-actions">
            <button className="btn btn-default back-to-results pointer" onClick={handleBack}>
              <ArrowLeft aria-hidden="true" size={18} strokeWidth={2} />
              {backAction.label}
            </button>
            <HeaderCartButton />
          </div>

          <div className="search-result-detail-layout">
            <div id="update-type-selection-region" />
            <div id="program-site-user-note-region" />

            <div className="search-detail-overview">
              <aside className="search-detail-summary card-highlight">

                <h1 className="search-detail-title">{displayTitle}</h1>
                {(displaySite || displayAgency) && (
                  <p className="search-detail-kicker">at {displaySite || displayAgency}</p>
                )}


                {(distanceLabel || summaryFacts.length > 0) && (
                  <div className="search-detail-quick-facts">
                    {distanceLabel && (
                      <div className="search-detail-distance">{distanceLabel}</div>
                    )}
                    {summaryFacts.map((item) => {
                      const Icon = item.icon;

                      return (
                        <div className="search-detail-quick-fact" key={item.label}>
                          <Icon className="search-detail-quick-fact-icon" aria-hidden="true" size={17} strokeWidth={1.8} />
                          <span className="search-detail-quick-fact-value">{item.value}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="search-detail-actions">
                  {directionsUrl && (
                    <DetailActionLink
                      href={directionsUrl}
                      onClick={() => searchService.trackActivity({ action: "directions", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })}
                      icon={MapPin}
                      label="Directions"
                    />
                  )}
                  {website && (
                    <DetailActionLink
                      href={website}
                      onClick={() => searchService.trackActivity({ action: "website", name: result.name, site_name: result.site_name, program_at_site_number: result.program_site_id })}
                      icon={Globe}
                      label="Website"
                    />
                  )}
                  {isSignedIn ? (
                    <div className="search-detail-action-embed" aria-label="Add to a List">
                      <AddToList result={result} compact />
                      <span className="search-detail-action-label">Add to List</span>
                    </div>
                  ) : (
                    <DetailActionButton onClick={handleCartToggle} icon={inCart ? Trash2 : Bookmark} label={inCart ? "Remove" : "Add to List"} />
                  )}
                  <div className="search-detail-action-embed" aria-label="Print and Share">
                    <ShareResult result={result} compact />
                    <span className="search-detail-action-label">Share</span>
                  </div>
                </div>

                {showAccountCta && !isSignedIn && (
                  <div className="create-account-cta-region search-detail-create-account-cta">
                    <CreateAccountCta onDismiss={() => setShowAccountCta(false)} />
                  </div>
                )}

                {showFeedbackBlock && (
                  <div className="search-detail-feedback-block">
                    <p>Feel like something is missing?</p>
                    {showSuggestionLinks && ((isSignedIn && notesEnabled) || organization.links?.claimResource) && (
                      <a className="search-detail-feedback-link update-listing-button" href="#" onClick={handleUpdateListing}>
                        <PencilLine aria-hidden="true" size={15} strokeWidth={2} />
                        <span>Update this listing</span>
                      </a>
                    )}
                    {showReferral && <SendReferral result={result} variant="feedback-link" isDetailPage />}
                  </div>
                )}

                {showClaimAgency && (
                  <div className="search-detail-feedback-block search-detail-feedback-block--secondary">
                    <p>Manage this resource?</p>
                    <a className="claim-agency-button" href="#" onClick={handleClaimAgency}>
                      Claim your agency
                    </a>
                  </div>
                )}
              </aside>

              <div className="search-detail-main">
                {(result.description || result.eligibility || (result.taxonomies && result.taxonomies.length > 0)) && (
                  <section className="search-detail-overview-group">
                    {result.description && (
                      <DetailInfoBlock label="Description">
                        <div dangerouslySetInnerHTML={sanitizedHtml(result.description)} />
                      </DetailInfoBlock>
                    )}

                    {result.eligibility && (
                      <DetailInfoBlock label="Eligibility">
                        <div dangerouslySetInnerHTML={sanitizedHtml(result.eligibility)} />
                      </DetailInfoBlock>
                    )}

                    {result.taxonomies && result.taxonomies.length > 0 && (
                      <DetailInfoBlock label="Categories">
                        <div className="search-detail-taxonomy-links">
                          {result.taxonomies.map((taxonomy, index) => (
                            <React.Fragment key={taxonomy.code}>
                              {index > 0 && <span className="search-detail-taxonomy-separator">, </span>}
                              <a
                                className="virtual"
                                href={`/search?terms=${encodeURIComponent(taxonomy.name)}&page=1&location=${encodeURIComponent(searchParams.location || "")}&taxonomy_code=${encodeURIComponent(taxonomy.code)}`}
                              >
                                {taxonomy.name}
                              </a>
                            </React.Fragment>
                          ))}
                        </div>
                      </DetailInfoBlock>
                    )}
                  </section>
                )}

                <div className="program-site-user-notes-region search-detail-user-notes-region">
                  <UserNotes key={userNotesKey} programSiteId={result.program_site_id} />
                </div>

                {detailItems.length > 0 && (
                  <section className="search-detail-data-grid-section">
                    <dl className="search-detail-data-grid">
                      {detailItems.map((item) => (
                        <DetailDataItem key={item.label} label={item.label} className={item.className}>
                          {item.content}
                        </DetailDataItem>
                      ))}
                    </dl>
                  </section>
                )}

                <AgencyInfo agencyId={result.agency_id} />

                <Articles programSiteId={result.program_site_id} />
              </div>
            </div>
          </div>

        </div>{/* results-content */}

      </div>{/* sidebar */}

    <ClaimAgencyModal
      agencyName={result.agency_name}
      agencyNumber={result.agency_number}
      show={claimModalOpen}
      onClose={() => setClaimModalOpen(false)}
    />

    <ListingUpdateTypeSelection
      show={showUpdateTypeModal}
      onClose={() => setShowUpdateTypeModal(false)}
      onShowNoteForm={handleShowNoteForm}
      reportIssueLink={getReportIssueLink()}
    />

    <ProgramSiteUserNoteForm
      show={showNoteFormModal}
      onClose={() => setShowNoteFormModal(false)}
      programSiteId={result.program_site_id}
      onNoteCreated={handleNoteCreated}
    />
    </div>
  );
}

export { getBackAction };
export default SearchResultDetailPage;
